import { env } from './env';

/**
 * Signing in with Google, for installs whose customer signs in from Google Workspace.
 *
 * ## Why Google Identity Services, and not an OAuth code flow
 *
 * GIS hands the BROWSER a signed ID token. The API already validates exactly that — issuer,
 * signature against Google's JWKS, `aud` against this install's client id, and the `hd` claim
 * against the customer's allowed domains. So there is nothing to exchange and nothing to keep:
 *
 * - **no client secret in the browser**, which would be impossible to keep anyway;
 * - **no server-side token exchange**, so no callback route and no second round trip;
 * - the customer's Google administrator registers one JavaScript origin, nothing more.
 *
 * ## Why Google's OWN rendered button, and not our styled one
 *
 * This is the correction of a real defect, so it is worth stating plainly rather than leaving the
 * next person to rediscover it. The first version put a styled button of ours on the page and
 * called `google.accounts.id.prompt()` in its click handler. Pressing it did NOTHING — no
 * redirect, no prompt, no error.
 *
 * Two compounding reasons, both confirmed against the live site in a real browser:
 *
 *  1. `prompt()` is **One Tap**, which is meant to appear by itself on page load, not to serve a
 *     click. Google documents that it "might not be displayed" depending on the person's session
 *     and account settings, that it can be opted out of globally or by a browser setting, and that
 *     dismissing it suppresses it for increasingly long periods. A sign-in button that works only
 *     when One Tap feels like appearing is not a sign-in button.
 *  2. Under FedCM — now mandatory — the moment listener's display methods (`isNotDisplayed`,
 *     `getNotDisplayedReason`) are **gone**. Those were exactly what reported the failure. Google's
 *     logger says so out loud: "uses one of the Google One Tap prompt UI status methods that may
 *     stop functioning when FedCM becomes mandatory". So the promise never settled, and the
 *     failure had no way to reach the screen. The observed console error was
 *     `FedCM get() rejects with NetworkError: Error retrieving a token.`
 *
 * `renderButton` has none of that: Google renders its own button into an element we give it, opens
 * its own account chooser on click, and delivers the credential to the same callback. Google's docs
 * note the browser's third-party sign-in setting "has no impact on the FedCM button flow". It also
 * satisfies Google's branding requirements, which our own button did not.
 *
 * One Tap is deliberately NOT used at all, not even as an extra: every failure mode above comes
 * back with it.
 *
 * ## The cost, stated plainly
 *
 * An ID token lasts about an hour and GIS issues no refresh token. So a Google session expires and
 * the person signs in again, where a Microsoft one renews silently through MSAL. The API answers
 * 401 when a token has expired, which is the signal to show the button again. Silent renewal for
 * Google would need the code flow and the secret, and is deliberately not built yet.
 */

const GIS_SRC = 'https://accounts.google.com/gsi/client';

/** The shape of the global GIS injects. Only what this module uses. */
interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize(options: {
        client_id: string;
        callback: (response: { credential?: string }) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
      }): void;
      renderButton(parent: HTMLElement, options: GoogleButtonOptions): void;
      disableAutoSelect(): void;
    };
  };
}

/** The subset of Google's button options this app sets. */
export interface GoogleButtonOptions {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'small' | 'medium' | 'large';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  /** Pixels. Google clamps this to 400; it does not accept a percentage. */
  width?: number;
}

declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

/** True when this install offers Google sign-in AND the API judged it usable. */
export function googleSignInAvailable(): boolean {
  return env.customerDirectory === 'google' && env.googleClientId.length > 0;
}

let scriptPromise: Promise<void> | null = null;

/**
 * Load the GIS script once, whatever the number of callers.
 *
 * Rejects rather than hanging if it cannot load — an offline page or a network that blocks
 * accounts.google.com must surface as a message, not a button that does nothing when pressed.
 */
export function loadGoogleScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    const script = existing ?? document.createElement('script');
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener(
      'error',
      () => {
        // Clear the cache so a later attempt can retry rather than resolving a dead promise.
        scriptPromise = null;
        reject(new Error('Could not reach Google to sign in.'));
      },
      { once: true },
    );
    if (!existing) {
      script.src = GIS_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  });
  return scriptPromise;
}

/** Where the credential goes when Google delivers one. Set by {@link mountGoogleButton}. */
let onCredential: ((token: string) => void) | null = null;
let initializedFor: string | null = null;

/**
 * Load GIS, initialise it, and render Google's button into `parent`.
 *
 * Every failure path throws with something a person can read: the whole point of this rewrite is
 * that pressing sign-in can no longer do nothing silently.
 *
 * `initialize` is called once per client id. Calling it repeatedly makes GIS log "called multiple
 * times … only the last initialized instance will be used", which is both noisy and a real hazard
 * if the callbacks differ — so the client id it was initialised for is remembered.
 */
export async function mountGoogleButton(
  parent: HTMLElement,
  options: GoogleButtonOptions,
  onToken: (token: string) => void,
): Promise<void> {
  if (!googleSignInAvailable()) {
    throw new Error('Google sign-in is not configured on this site.');
  }
  await loadGoogleScript();
  const gis = window.google?.accounts?.id;
  if (!gis) throw new Error('Could not reach Google to sign in.');

  // Latest wins: the component remounts on re-render, and the freshest callback is the live one.
  onCredential = onToken;

  if (initializedFor !== env.googleClientId) {
    gis.initialize({
      client_id: env.googleClientId,
      callback: (response) => {
        if (!response.credential) return;
        onCredential?.(response.credential);
      },
      // No silent auto-select: on a shared machine it would sign in whoever used it last, with no
      // chance to choose. The person presses the button and picks an account.
      auto_select: false,
    });
    initializedFor = env.googleClientId;
  }

  parent.replaceChildren();
  gis.renderButton(parent, options);
}

/** Forget the chosen account, so the next sign-in asks again. Safe to call when GIS never loaded. */
export function forgetGoogleAccount(): void {
  try {
    window.google?.accounts?.id?.disableAutoSelect();
  } catch {
    // Nothing to forget.
  }
}

/** Test seam: reset the module's one-time state between cases. */
export function __resetGoogleSignInForTests(): void {
  scriptPromise = null;
  initializedFor = null;
  onCredential = null;
}

/**
 * Where the Google ID token lives between requests.
 *
 * `sessionStorage`, not `localStorage`: the token is good for about an hour, so outliving the tab
 * buys nothing and leaving a credential in a shared browser costs something. It survives a page
 * refresh, which is the case that matters — without it, reloading the page signs the person out.
 *
 * Every access is wrapped: a private window, blocked site data or a storage quota error throws on
 * read as readily as on write, and that must degrade to "not signed in", never to a crash on boot.
 */
const TOKEN_KEY = 'mg.googleIdToken';

export function readStoredGoogleToken(): string | null {
  try {
    return window.sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function storeGoogleToken(token: string): void {
  try {
    window.sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Storage unavailable: the token still works for this page's lifetime, held in memory by the
    // auth layer. A refresh will ask again, which is the right failure.
  }
}

export function clearStoredGoogleToken(): void {
  try {
    window.sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // Nothing to clear.
  }
}

/**
 * Whether a stored ID token has already expired, read from its own `exp` claim WITHOUT verifying
 * the signature — this is a "should we even try" check, not a security decision. The API verifies
 * properly, and refuses anything expired.
 *
 * Checking here avoids a pointless 401 on every boot after an hour away, which would otherwise
 * look like a broken session rather than an ordinary expiry.
 */
export function googleTokenExpired(token: string, nowMs: number): boolean {
  try {
    const [, payload] = token.split('.');
    if (!payload) return true;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    if (typeof json.exp !== 'number') return true;
    // 30s of slack, so a token about to expire mid-flight is treated as gone.
    return json.exp * 1000 <= nowMs + 30_000;
  } catch {
    // Unparsable means unusable.
    return true;
  }
}
